import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import Drawer from '../../Drawer';
import { inputStyles, labelStyles } from './styles';
import { putCampaignRequestPrimaryContact } from '~/services/campaign';

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    campaignRequest: any;
    metadata: any;
    onUpdate?: (response: any) => void;
}

interface SelectOption {
    value: number | string;
    label: string;
}

const errorTextClass = 'mt-1 text-sm text-[#ff4b4b]';

const US_STATES: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

const stateOptions = Object.entries(US_STATES).map(([value, label]) => ({
    value,
    label,
}));

const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    if (digits.length >= 1) formatted = `(${digits.slice(0, 3)}`;
    if (digits.length >= 4) formatted += `) ${digits.slice(3, 6)}`;
    if (digits.length >= 7) formatted += `-${digits.slice(6, 10)}`;
    return formatted;
};

interface FormValues {
    primary_first_name: string;
    primary_middle_name: string;
    primary_last_name: string;
    primary_email: string;
    primary_phone: string;
    primary_address_1: string;
    primary_address_2: string;
    primary_city: string;
    primary_state: SelectOption | null;
    primary_zip_code: string;
    primary_relationship_to_client: SelectOption | null;
    primary_is_emergency_contact: boolean;
}

const PrimaryContactInfoDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    campaignRequest,
    metadata,
    onUpdate,
}) => {
    const methods = useForm<FormValues>({
        defaultValues: {
            primary_first_name: '',
            primary_middle_name: '',
            primary_last_name: '',
            primary_email: '',
            primary_phone: '',
            primary_address_1: '',
            primary_address_2: '',
            primary_city: '',
            primary_state: null,
            primary_zip_code: '',
            primary_relationship_to_client: null,
            primary_is_emergency_contact: false,
        },
    });

    const { reset, handleSubmit, control, formState, setValue, watch } = methods;
    const { errors } = formState;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSameAsClient, setIsSameAsClient] = useState(false);

    const handleClose = () => {
        if (
            formState.isDirty &&
            !window.confirm(
                'You have unsaved changes. Are you sure you want to close?',
            )
        ) {
            return;
        }
        setIsOpen(false);
    };

    const relationshipOptions = useMemo(
        () =>
            (metadata?.campaign_is_for || [])
                .filter(
                    (item: any) => typeof item === 'object' && item !== null,
                )
                .map((item: any) => ({ value: item?.id, label: item?.name })),
        [metadata],
    );

    const selfOption = useMemo(() => {
        const selfItem = (metadata?.campaign_is_for || []).find(
            (item: any) => item?.code === 'self',
        );
        return selfItem ? { value: selfItem.id, label: selfItem.name } : null;
    }, [metadata]);

    // The initial contact IS the client, so copying from them is meaningless.
    const isCampaignForSelf = campaignRequest?.campaign_is_for?.code === 'self';

    // A "self" relationship means the backend owns these fields:
    // CampaignRequest.sync_primary_contact_from_client (BEFORE_SAVE) copies every
    // patient_* value over its primary_* counterpart on save. So the relationship
    // has to be cleared to break the link — editing the fields alone gets reverted.
    const selectedRelationship = watch('primary_relationship_to_client');
    const isPrimaryContactSelf =
        selfOption != null && selectedRelationship?.value === selfOption.value;
    const isClientLinked = isSameAsClient || isPrimaryContactSelf;

    const resolveStateOption = (stateObj: any): SelectOption | null => {
        if (!stateObj) return null;
        const abbr = stateObj.abbreviation || stateObj.value;
        if (abbr && US_STATES[abbr]) {
            return { value: abbr, label: US_STATES[abbr] };
        }
        const stateName = stateObj.state || stateObj.label || stateObj.name;
        if (stateName) {
            const found = stateOptions.find((opt) => opt.label === stateName);
            if (found) return found;
        }
        if (stateObj.id) {
            return {
                value: stateObj.id,
                label: stateName || String(stateObj.id),
            };
        }
        return null;
    };

    useEffect(() => {
        if (!campaignRequest) return;

        const isSelf =
            campaignRequest?.primary_relationship_to_client?.code === 'self';
        setIsSameAsClient(isSelf);

        // When primary contact is "self", populate from patient/client fields
        // to handle legacy records that were saved with empty primary fields.
        const usePatient = isSelf;

        reset({
            primary_first_name: usePatient
                ? campaignRequest?.patient_first_name || ''
                : campaignRequest?.primary_first_name || '',
            primary_middle_name: usePatient
                ? campaignRequest?.patient_middle_name || ''
                : campaignRequest?.primary_middle_name || '',
            primary_last_name: usePatient
                ? campaignRequest?.patient_last_name || ''
                : campaignRequest?.primary_last_name || '',
            primary_email: usePatient
                ? campaignRequest?.patient_email || ''
                : campaignRequest?.primary_email || '',
            primary_phone: usePatient
                ? campaignRequest?.patient_phone1 || ''
                : campaignRequest?.primary_phone || '',
            primary_address_1: usePatient
                ? campaignRequest?.patient_address_1 || ''
                : campaignRequest?.primary_address_1 || '',
            primary_address_2: usePatient
                ? campaignRequest?.patient_address_2 || ''
                : campaignRequest?.primary_address_2 || '',
            primary_city: usePatient
                ? campaignRequest?.patient_city || ''
                : campaignRequest?.primary_city || '',
            primary_state: usePatient
                ? resolveStateOption(campaignRequest?.patient_state)
                : resolveStateOption(campaignRequest?.primary_state),
            primary_zip_code: usePatient
                ? campaignRequest?.patient_zip_code || ''
                : campaignRequest?.primary_zip_code || '',
            primary_relationship_to_client:
                campaignRequest?.primary_relationship_to_client
                    ? {
                          value: campaignRequest.primary_relationship_to_client
                              .id,
                          label: campaignRequest.primary_relationship_to_client
                              .name,
                      }
                    : null,
            primary_is_emergency_contact:
                campaignRequest?.primary_is_emergency_contact || false,
        });
    }, [campaignRequest, reset, isOpen]);

    const dirty = { shouldDirty: true };

    // The initial contact only has a name, email and phone on the record — there
    // are no initial_* address fields. Anything else still holding the client's
    // data has to be cleared or we'd save the parent's name over the child's address.
    const copyFromInitialContact = () => {
        setIsSameAsClient(false);
        setValue(
            'primary_first_name',
            campaignRequest?.initial_first_name || '',
            dirty,
        );
        setValue('primary_middle_name', '', dirty);
        setValue(
            'primary_last_name',
            campaignRequest?.initial_last_name || '',
            dirty,
        );
        setValue('primary_email', campaignRequest?.initial_email || '', dirty);
        setValue('primary_phone', campaignRequest?.initial_phone || '', dirty);
        setValue('primary_address_1', '', dirty);
        setValue('primary_address_2', '', dirty);
        setValue('primary_city', '', dirty);
        setValue('primary_state', null, dirty);
        setValue('primary_zip_code', '', dirty);
        // campaign_is_for is the initial contact's relationship *to* the client
        // ("Parent" when a mother opens a request for her son), which is exactly
        // what primary_relationship_to_client wants. Clear it when absent rather
        // than leaving a stale "self" that would re-arm the backend sync.
        setValue(
            'primary_relationship_to_client',
            campaignRequest?.campaign_is_for
                ? {
                      value: campaignRequest.campaign_is_for.id,
                      label: campaignRequest.campaign_is_for.name,
                  }
                : null,
            dirty,
        );
    };

    const unlinkFromClient = () => {
        setIsSameAsClient(false);
        // Clearing the relationship is what actually breaks the link; without it
        // the BEFORE_SAVE hook overwrites everything typed here on the next save.
        setValue('primary_relationship_to_client', null, dirty);
        // These fields are a guaranteed copy of the client (the hook enforces it
        // while linked), so blank them instead of leaving the client's details
        // sitting under a non-self relationship waiting to be saved by accident.
        setValue('primary_first_name', '', dirty);
        setValue('primary_middle_name', '', dirty);
        setValue('primary_last_name', '', dirty);
        setValue('primary_email', '', dirty);
        setValue('primary_phone', '', dirty);
        setValue('primary_address_1', '', dirty);
        setValue('primary_address_2', '', dirty);
        setValue('primary_city', '', dirty);
        setValue('primary_state', null, dirty);
        setValue('primary_zip_code', '', dirty);
    };

    const toggleSameAsClient = () => {
        if (isClientLinked) {
            unlinkFromClient();
            return;
        }
        setIsSameAsClient(true);
        setValue(
            'primary_first_name',
            campaignRequest?.patient_first_name || '',
            dirty,
        );
        setValue(
            'primary_middle_name',
            campaignRequest?.patient_middle_name || '',
            dirty,
        );
        setValue(
            'primary_last_name',
            campaignRequest?.patient_last_name || '',
            dirty,
        );
        setValue('primary_email', campaignRequest?.patient_email || '', dirty);
        setValue('primary_phone', campaignRequest?.patient_phone1 || '', dirty);
        setValue(
            'primary_address_1',
            campaignRequest?.patient_address_1 || '',
            dirty,
        );
        setValue(
            'primary_address_2',
            campaignRequest?.patient_address_2 || '',
            dirty,
        );
        setValue('primary_city', campaignRequest?.patient_city || '', dirty);
        setValue(
            'primary_state',
            resolveStateOption(campaignRequest?.patient_state),
            dirty,
        );
        setValue(
            'primary_zip_code',
            campaignRequest?.patient_zip_code || '',
            dirty,
        );
        // The client can't be their own emergency contact.
        setValue('primary_is_emergency_contact', false, dirty);
        if (selfOption) {
            setValue('primary_relationship_to_client', selfOption, dirty);
        }
    };

    const onSubmit = async (values: FormValues) => {
        setIsSubmitting(true);
        toast.loading('Saving...');
        try {
            const payload: any = {
                primary_first_name: values.primary_first_name || '',
                primary_middle_name: values.primary_middle_name || '',
                primary_last_name: values.primary_last_name || '',
                primary_email: values.primary_email || '',
                primary_phone: values.primary_phone || '',
                primary_address_1: values.primary_address_1 || '',
                primary_address_2: values.primary_address_2 || '',
                primary_city: values.primary_city || '',
                primary_zip_code: values.primary_zip_code || '',
                primary_is_emergency_contact:
                    values.primary_is_emergency_contact || false,
                primary_state_id: values.primary_state?.value ?? null,
                primary_relationship_to_client_id:
                    values.primary_relationship_to_client?.value ?? null,
            };

            const response = await putCampaignRequestPrimaryContact(
                campaignRequest?.id,
                payload,
            );

            toast.dismiss();
            toast.success('Primary contact updated successfully');
            onUpdate?.(response);
            setIsOpen(false);
        } catch (e: any) {
            toast.dismiss();
            console.error('Error updating primary contact information:', e);
            toast.error(
                e?.detail ||
                    'There was an error updating primary contact information',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const sameAsButtonClass =
        'rounded-full border-[2px] px-[16px] py-[5px] text-[13px]/[16px] font-bold transition-colors duration-300 hover:border-black hover:bg-black hover:text-white';

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onClose={handleClose}
            title='Primary Contact'
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Same As Buttons */}
                    <div className='mb-5'>
                        <label className={labelStyles}>Same as</label>
                        <div className='mt-1 flex gap-2'>
                            {!isCampaignForSelf && (
                                <button
                                    type='button'
                                    className={sameAsButtonClass}
                                    onClick={copyFromInitialContact}
                                >
                                    Initial Contact
                                </button>
                            )}
                            <button
                                type='button'
                                className={`${sameAsButtonClass} ${
                                    isClientLinked
                                        ? 'border-[#00AEB3] bg-[#00AEB3] text-white'
                                        : ''
                                }`}
                                onClick={toggleSameAsClient}
                                aria-pressed={isClientLinked}
                            >
                                Client
                            </button>
                        </div>
                    </div>

                    {isClientLinked ? (
                        <div className='mb-5 rounded-lg bg-[#F5F5F5] px-5 py-4 text-[14px]/[20px] text-[#131212]'>
                            <span className='font-semibold'>Note:</span> contact
                            information is shared from Client. Turn off{' '}
                            <span className='font-semibold'>Client</span> above
                            to enter a different primary contact.
                        </div>
                    ) : (
                        <>
                            {/* Emergency Contact Checkbox */}
                            <div className='mb-5'>
                                <Controller
                                    name='primary_is_emergency_contact'
                                    control={control}
                                    render={({ field }) => (
                                        <label className='flex cursor-pointer items-center gap-2'>
                                            <input
                                                type='checkbox'
                                                checked={field.value}
                                                onChange={(e) =>
                                                    field.onChange(
                                                        e.target.checked,
                                                    )
                                                }
                                                className='h-4 w-4 accent-[#00AEB3]'
                                            />
                                            <span className='text-[14px]/[18px]'>
                                                Make Emergency Contact
                                            </span>
                                        </label>
                                    )}
                                />
                            </div>

                            {/* Name Fields */}
                            <div className='mb-5 flex gap-3'>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='pcid-first-name'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        First Name
                                    </label>
                                    <Controller
                                        name='primary_first_name'
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                id='pcid-first-name'
                                                className={`w-full ${inputStyles}`}
                                                type='text'
                                                placeholder='Enter first name'
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='pcid-middle-name'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        Middle Name
                                    </label>
                                    <Controller
                                        name='primary_middle_name'
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                id='pcid-middle-name'
                                                className={`w-full ${inputStyles}`}
                                                type='text'
                                                placeholder='Enter middle name'
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='pcid-last-name'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        Last Name
                                    </label>
                                    <Controller
                                        name='primary_last_name'
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                id='pcid-last-name'
                                                className={`w-full ${inputStyles}`}
                                                type='text'
                                                placeholder='Enter last name'
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className='mb-5'>
                                <label
                                    htmlFor='pcid-email'
                                    className={`w-full ${labelStyles}`}
                                >
                                    Email
                                </label>
                                <Controller
                                    name='primary_email'
                                    control={control}
                                    rules={{
                                        pattern: {
                                            value: /^$|^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message:
                                                'Please enter a valid email address',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <input
                                            id='pcid-email'
                                            className={`w-full ${inputStyles}`}
                                            type='email'
                                            placeholder='email@website.com'
                                            {...field}
                                        />
                                    )}
                                />
                                {errors.primary_email && (
                                    <p className={errorTextClass}>
                                        {errors.primary_email.message}
                                    </p>
                                )}
                            </div>

                            {/* Contact Number */}
                            <div className='mb-5'>
                                <label
                                    htmlFor='pcid-phone'
                                    className={`w-full ${labelStyles}`}
                                >
                                    Contact Number
                                </label>
                                <Controller
                                    name='primary_phone'
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (
                                                value &&
                                                !/^\(\d{3}\) \d{3}-\d{4}$/.test(
                                                    value,
                                                )
                                            ) {
                                                return 'Please enter a valid phone number in format (123) 456-7890';
                                            }
                                            return true;
                                        },
                                    }}
                                    render={({ field }) => (
                                        <input
                                            id='pcid-phone'
                                            className={`w-full ${inputStyles}`}
                                            type='tel'
                                            placeholder='000 000 0000'
                                            maxLength={14}
                                            onChange={(e) =>
                                                field.onChange(
                                                    formatPhone(e.target.value),
                                                )
                                            }
                                            value={field.value}
                                        />
                                    )}
                                />
                                {errors.primary_phone && (
                                    <p className={errorTextClass}>
                                        {errors.primary_phone.message}
                                    </p>
                                )}
                            </div>

                            {/* Street Address 1 */}
                            <div className='mb-5'>
                                <label
                                    htmlFor='pcid-address-1'
                                    className={`w-full ${labelStyles}`}
                                >
                                    Street Address 1
                                </label>
                                <Controller
                                    name='primary_address_1'
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            id='pcid-address-1'
                                            className={`w-full ${inputStyles}`}
                                            type='text'
                                            placeholder='Select'
                                            {...field}
                                        />
                                    )}
                                />
                            </div>

                            {/* Street Address 2 */}
                            <div className='mb-5'>
                                <label
                                    htmlFor='pcid-address-2'
                                    className={`w-full ${labelStyles}`}
                                >
                                    Street Address 2
                                </label>
                                <Controller
                                    name='primary_address_2'
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            id='pcid-address-2'
                                            className={`w-full ${inputStyles}`}
                                            type='text'
                                            placeholder='Select'
                                            {...field}
                                        />
                                    )}
                                />
                            </div>

                            {/* City */}
                            <div className='mb-5'>
                                <label
                                    htmlFor='pcid-city'
                                    className={`w-full ${labelStyles}`}
                                >
                                    City
                                </label>
                                <Controller
                                    name='primary_city'
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            id='pcid-city'
                                            className={`w-full ${inputStyles}`}
                                            type='text'
                                            placeholder='Select'
                                            {...field}
                                        />
                                    )}
                                />
                            </div>

                            {/* State & Zip Code */}
                            <div className='mb-5 flex gap-3'>
                                <div className='flex-[3]'>
                                    <label
                                        htmlFor='pcid-state'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        State
                                    </label>
                                    <Controller
                                        name='primary_state'
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                menuPlacement='auto'
                                                id='pcid-state'
                                                className='w-full'
                                                options={stateOptions}
                                                isClearable
                                                placeholder='Select'
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className='flex-1'>
                                    <label
                                        htmlFor='pcid-zip'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        Zip Code
                                    </label>
                                    <Controller
                                        name='primary_zip_code'
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                id='pcid-zip'
                                                className={`w-full ${inputStyles}`}
                                                type='text'
                                                placeholder='00000'
                                                maxLength={10}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Relationship to Client */}
                            <div className='mb-5'>
                                <label
                                    htmlFor='pcid-relationship'
                                    className={`w-full ${labelStyles}`}
                                >
                                    Relationship to Client
                                </label>
                                <Controller
                                    name='primary_relationship_to_client'
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            menuPlacement='auto'
                                            id='pcid-relationship'
                                            className='w-full'
                                            options={relationshipOptions}
                                            isClearable
                                            placeholder='Select'
                                            {...field}
                                        />
                                    )}
                                />
                            </div>
                        </>
                    )}

                    {/* Action Buttons */}
                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </FormProvider>
        </Drawer>
    );
};

export default PrimaryContactInfoDrawer;
